import { Stack } from '@mantine/core';
import Card from 'components/Card';
import DataTables from 'components/DataTables';
import Toolbar from 'components/Toolbar';
import React, { useState, useEffect } from 'react';
import { columns as COLUMN_MODULE_MANAGEMENT } from './schema';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus } from '@tabler/icons-react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { pathnames } from 'routes/dacum';
import { useModuleManagementStore } from 'store/moduleManagement';
import { CompetencyModuleData } from 'types/dacum/moduleManagement';
import ModalDelete from 'modules/pages/lcms/courses/ModalDelete';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { moduleManagementService } from 'services/dacum/module.service';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';

interface ModuleParams {
  search?: string;
  status?: string | null;
  startDate?: Date | null;
  endDate?: Date | null;
  page?: number;
  size?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

const TableModuleManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [openedModalDelete, { open: openModalDelete, close: closeModalDelete }] =
    useDisclosure(false);
  const [columns, setColumns] = useState(COLUMN_MODULE_MANAGEMENT);
  const { formData, setMode, setFormData, reset } = useModuleManagementStore();
  const [selectedRow, setSelectedRow] = useState<CompetencyModuleData | null>(null);
  const [params, setParams] = useState<ModuleParams>({});

  const search = searchParams.get('search');
  const status = searchParams.get('status');
  const sortBy = searchParams.get('sortBy');
  const sortOrder = searchParams.get('sortOrder');
  const pageNumber = searchParams.get('page');
  const pageSize = searchParams.get('size');
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');

  const { data, isLoading } = useQuery({
    queryKey: [
      'moduleCompetency',
      search,
      status,
      sortBy,
      sortOrder,
      pageNumber,
      pageSize,
      startDate,
      endDate
    ],
    queryFn: () =>
      moduleManagementService.getModuleCompetency({
        search: search ?? undefined,
        startDate: startDate ?? undefined,
        endDate: endDate ?? undefined,
        status: status ?? undefined,
        sortBy: sortBy ?? undefined,
        sortOrder: (sortOrder as 'asc' | 'desc') ?? 'asc',
        pageNumber: Number(pageNumber) || 1,
        pageSize: Number(pageSize) || 10
      })
  });

  const deleteModule = useMutation({
    mutationFn: moduleManagementService.deleteModule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moduleCompetency'] });
      closeModalDelete();
      setSelectedRow(null);
      showSuccessNotification({
        title: 'Success',
        message: 'Module deleted successfully'
      });
    },
    onError: (error) => {
      showErrorNotification({
        title: 'Error when delete module',
        message: error.message
      });
    }
  });

  useEffect(() => {
    const urlParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        if (value instanceof Date) {
          urlParams.set(key, value.toISOString());
        } else {
          urlParams.set(key, value.toString());
        }
      }
    });
    navigate(`${location.pathname}?${urlParams.toString()}`);
  }, [params, navigate, location.pathname]);

  const handleChangeSize = (size: number) =>
    setParams((prevState) => {
      return { ...prevState, size };
    });

  const handleChangePage = (page: number) =>
    setParams((prevState) => {
      return { ...prevState, page };
    });

  const onDelete = () => {
    return (
      selectedRow &&
      deleteModule.mutate({
        trCompetencyModuleID: selectedRow.trCompetencyModuleID
      })
    );
  };

  const handleEdit = (row: CompetencyModuleData) => {
    setMode('edit');
    setFormData({
      trCompetencyModuleID: row.trCompetencyModuleID,
      msCompetencyManagementID: row.msCompetencyManagementID,
      msLCMSCourseID: row.msLCMSCourseID,
      deliveryMode: row.deliveryMode,
      liaType: row.liaType,
      courseName: row.courseName,
      dateIn: row.dateIn,
      userIn: row.userIn
    });
    navigate(`${pathnames.editCompetencyModule.replace(':id', row.trCompetencyModuleID)}`);
  };

  const handleDelete = (row: CompetencyModuleData) => {
    setSelectedRow(row);
    openModalDelete();
  };

  const handleView = (row: CompetencyModuleData) => {
    setMode('view');
    setFormData({
      trCompetencyModuleID: row.trCompetencyModuleID,
      msCompetencyManagementID: row.msCompetencyManagementID,
      msLCMSCourseID: row.msLCMSCourseID,
      deliveryMode: row.deliveryMode,
      liaType: row.liaType,
      courseName: row.courseName,
      dateIn: row.dateIn,
      userIn: row.userIn
    });
    navigate(`${pathnames.competencyModuleDetail.replace(':id', row.trCompetencyModuleID)}`);
  };

  return (
    <>
      <Card>
        <Stack>
          <Toolbar
            columns={columns}
            showFilterColumn
            setColumns={setColumns}
            createButtonText='New Module'
            buttonCreateLeftSection={<IconPlus />}
            onCreate={() => {
              reset();
              navigate(pathnames.createCompetencyModule);
            }}
          />

          <DataTables
            columns={columns}
            data={data?.data || []}
            loading={isLoading}
            totalData={data?.totalRecords}
            currentPage={Number(params?.page)}
            currentSize={Number(params?.size)}
            withBulkData
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleView={handleView}
            handleChangeSize={handleChangeSize}
            handleChangePage={handleChangePage}
            emptyMessageTitle='No Module Data Found'
            emptyMessageDescription='Please try again later'
          />
        </Stack>
      </Card>

      <ModalDelete
        title='Delete Module'
        description={`You are about to delete module ${selectedRow?.courseName || formData?.courseName}. Are you sure to delete it?`}
        labelConfirm='Delete'
        onClose={closeModalDelete}
        onConfirm={onDelete}
        opened={openedModalDelete}
        isLoading={deleteModule.isPending}
      />
    </>
  );
};

export default TableModuleManagement;
